import { getAllReplies } from './dataFetch';

/**
 * 이메일 주소를 추출하는 정규식
 * 골뱅이(@)를 포함한 이메일 형식을 찾습니다
 */
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

/**
 * 텍스트에서 이메일 주소를 추출합니다
 * @param {string} text - 검색할 텍스트
 * @returns {string[]} - 찾은 이메일 주소 배열
 */
export const extractEmailsFromText = (text) => {
  if (!text) return [];
  const matches = text.match(EMAIL_REGEX);
  return matches ? [...new Set(matches)] : []; // 중복 제거
};

/**
 * 모든 댓글을 가져옵니다 (페이지네이션, 대댓글 제외)
 * @param {string} threadId - 스레드 ID
 * @param {Function} onProgress - 진행 상황 콜백
 * @returns {Promise<Array>} - 모든 댓글 객체 배열
 */
export const fetchAllRepliesRecursively = async (threadId, depth = 0, onProgress = null) => {
  try {
    if (onProgress) {
      onProgress(0, 0, 'Fetching all replies...');
    }

    const replies = await getAllReplies(threadId);

    if (!replies || replies.length === 0) {
      return [];
    }

    if (onProgress) {
      onProgress(replies.length, replies.length, `Fetched ${replies.length} replies`);
    }

    return replies;
  } catch (error) {
    console.error(`Error fetching replies for thread ${threadId}:`, error);
    return [];
  }
};

/**
 * 모든 댓글을 가져옵니다 (페이지네이션 + 대댓글 포함)
 * @param {string} threadId - 스레드 ID
 * @param {Function} onProgress - 진행 상황 콜백
 * @returns {Promise<Array>} - 모든 댓글 객체 배열
 */
export const fetchAllRepliesWithNested = async (threadId, depth = 0, onProgress = null) => {
  try {
    if (onProgress) {
      onProgress(0, 0, 'Fetching nested replies...');
    }

    const visitedReplies = new Set();
    const processedTargets = new Set();
    const allReplies = [];

    const fetchRepliesForTarget = async (targetId, currentDepth) => {
      if (!targetId || processedTargets.has(targetId)) {
        return;
      }

      processedTargets.add(targetId);
      const nextDepth = currentDepth + 1;
      if (onProgress) {
        const depthLabel = currentDepth === 0 ? 'top-level replies' : `depth ${nextDepth} replies`;
        onProgress(allReplies.length, allReplies.length, `Fetching ${depthLabel}...`);
      }

      let replies = [];
      try {
        replies = await getAllReplies(targetId);
      } catch (error) {
        console.error(`Error fetching replies for target ${targetId}:`, error);
        return;
      }

      if (!replies || replies.length === 0) {
        return;
      }

      replies.forEach((reply) => {
        if (visitedReplies.has(reply.id)) {
          return;
        }
        visitedReplies.add(reply.id);
        allReplies.push({
          ...reply,
          parentId: targetId,
          depth: nextDepth,
        });
      });

      if (onProgress) {
        onProgress(
          allReplies.length,
          allReplies.length,
          `Collected ${allReplies.length} replies so far`
        );
      }

      for (const reply of replies) {
        await fetchRepliesForTarget(reply.id, nextDepth);
      }
    };

    const initialDepth = Number.isFinite(depth) ? depth : 0;
    await fetchRepliesForTarget(threadId, initialDepth);

    if (onProgress) {
      onProgress(
        allReplies.length,
        allReplies.length,
        `Fetched ${allReplies.length} replies across all depths`
      );
    }

    return allReplies;
  } catch (error) {
    console.error(`Error fetching nested replies for thread ${threadId}:`, error);
    return [];
  }
};

/**
 * 스레드의 모든 댓글에서 이메일 주소를 추출합니다
 * @param {string} threadId - 스레드 ID
 * @param {Function} onProgress - 진행 상황 콜백 (current, total, message)
 * @returns {Promise<Object>} - { emails: string[], repliesCount: number, emailsWithContext: Array }
 */
export const extractEmailsFromThread = async (threadId, onProgress = null, options = {}) => {
  try {
    const { includeNested = false } = options || {};

    // 진행 상황 콜백 래퍼
    const progressWrapper = (current, total, message) => {
      if (onProgress) {
        onProgress(current, total, message);
      }
    };

    // 모든 댓글 가져오기 (페이지네이션 포함)
    if (onProgress) {
      onProgress(0, 0, 'Starting to fetch replies...');
    }

    const repliesFetcher = includeNested
      ? fetchAllRepliesWithNested
      : fetchAllRepliesRecursively;

    const allReplies = await repliesFetcher(threadId, 0, progressWrapper);

    if (allReplies.length === 0) {
      return {
        emails: [],
        repliesCount: 0,
        emailsWithContext: []
      };
    }

    // 각 댓글에서 이메일 추출
    const emailsWithContext = [];
    const allEmails = [];

    if (onProgress) {
      onProgress(0, allReplies.length, 'Extracting emails...');
    }

    allReplies.forEach((reply, index) => {
      if (onProgress) {
        onProgress(index + 1, allReplies.length, `Processing ${index + 1} of ${allReplies.length} replies...`);
      }

      const emails = extractEmailsFromText(reply.text);
      if (emails.length > 0) {
        emails.forEach(email => {
          allEmails.push(email);
          emailsWithContext.push({
            email,
            username: reply.username,
            text: reply.text,
            replyId: reply.id,
            depth: reply.depth ?? 1,
            parentId: reply.parentId || threadId
          });
        });
      }
    });

    // 중복 제거된 이메일 목록
    const uniqueEmails = [...new Set(allEmails)];

    if (onProgress) {
      onProgress(allReplies.length, allReplies.length, `Complete! Found ${uniqueEmails.length} unique emails.`);
    }

    return {
      emails: uniqueEmails,
      repliesCount: allReplies.length,
      emailsWithContext
    };
  } catch (error) {
    console.error('Error extracting emails from thread:', error);
    throw error;
  }
};

/**
 * 이메일 목록을 지정된 크기로 그룹화합니다
 * @param {string[]} emails - 이메일 배열
 * @param {number} groupSize - 그룹 크기
 * @returns {string[][]} - 그룹화된 이메일 배열
 */
export const groupEmails = (emails, groupSize) => {
  if (!emails || emails.length === 0 || groupSize <= 0) {
    return [];
  }

  const groups = [];
  for (let i = 0; i < emails.length; i += groupSize) {
    groups.push(emails.slice(i, i + groupSize));
  }
  return groups;
};

/**
 * 이메일 목록을 CSV 형식 문자열로 변환합니다
 * @param {string[]} emails - 이메일 배열
 * @returns {string} - CSV 형식 문자열
 */
export const emailsToCSV = (emails) => {
  if (!emails || emails.length === 0) {
    return '';
  }
  return 'Email Address\n' + emails.map(email => `"${email}"`).join('\n');
};

/**
 * 컨텍스트와 함께 이메일 목록을 CSV 형식 문자열로 변환합니다
 * @param {Array} emailsWithContext - 이메일과 컨텍스트 정보 배열
 * @returns {string} - CSV 형식 문자열
 */
export const emailsWithContextToCSV = (emailsWithContext) => {
  if (!emailsWithContext || emailsWithContext.length === 0) {
    return '';
  }

  const header = 'Email,Username,Comment Text,Reply ID\n';
  const rows = emailsWithContext.map(item => {
    const text = item.text.replace(/"/g, '""').replace(/\n/g, ' ');
    return `"${item.email}","${item.username}","${text}","${item.replyId}"`;
  }).join('\n');

  return header + rows;
};
